import { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';

type Props = {
  open?: boolean;
  children: React.ReactNode;
  allowSave?: boolean;
  onSave?: () => void;
  onCancel: () => void;
  saveText?: string;
  showButtons?: boolean;
  extraWide?: boolean;
};

const Modal = ({
  children,
  open = false,
  allowSave = true,
  saveText = 'Save Changes',
  onSave,
  onCancel,
  showButtons,
  extraWide = false
}: Props) => {
  showButtons = showButtons ?? true;

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as='div'
        className='fixed inset-0 z-10 overflow-y-auto'
        // Passing empty function keeps dialog open when user clicks outside it.
        // Close function is handled exclusively by "cancel" button.
        onClose={() => { }}
      >
        <div className='flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0'>
          <Transition.Child
            as={Fragment}
            enter='ease-out duration-300'
            enterFrom='opacity-0'
            enterTo='opacity-100'
            leave='ease-in duration-200'
            leaveFrom='opacity-100'
            leaveTo='opacity-0'
          >
            <Dialog.Overlay className='fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75' />
          </Transition.Child>

          {/* This element is to trick the browser into centering the modal contents. */}
          <span
            className='hidden sm:inline-block sm:align-middle sm:h-screen'
            aria-hidden='true'
          >
            &#8203;
          </span>
          <Transition.Child
            as={Fragment}
            enter='ease-out duration-300'
            enterFrom='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
            enterTo='opacity-100 translate-y-0 sm:scale-100'
            leave='ease-in duration-200'
            leaveFrom='opacity-100 translate-y-0 sm:scale-100'
            leaveTo='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
          >
            <div className={`inline-block text-left align-bottom transition-all transform bg-white rounded-lg shadow-xl sm:my-8 sm:align-middle ${extraWide ? 'sm:max-w-xl' : 'sm:max-w-full'} sm:w-full lg:w-4/12`}>
              {/* if we aren't showing buttons don't wrap the content in a padding, assuming the parent will handle that */}
              {!showButtons && (
                <div>
                  {children}
                </div>
              )}

              {/* if we are showing the buttons then show the content inside of padding */}
              {showButtons && (
                <Fragment>
                  <div className='px-4 pt-5 pb-4 bg-white sm:p-6 sm:pb-4'>
                    {children}
                  </div>
                  <div className='px-4 py-3 bg-gray-50 sm:px-6 sm:flex sm:flex-row-reverse'>
                    {onSave ?
                      <button
                        type='button'
                        disabled={!allowSave}
                        className='inline-flex justify-center w-full px-4 py-2 text-base font-medium text-white transition duration-150 ease-in-out border border-transparent rounded-md shadow-sm bg-ptsblue-normal hover:bg-ptshover hover:text-black focus:outline-none sm:ml-3 sm:w-auto sm:text-sm'
                        onClick={onSave}
                      >
                        {saveText}
                      </button>
                      :
                      <></>
                    }
                    <button
                      type='button'
                      className='inline-flex justify-center w-full px-4 py-2 mt-3 text-base font-medium text-gray-700 transition duration-150 ease-in-out bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm'
                      onClick={onCancel}
                    >
                      Cancel
                    </button>
                  </div>
                </Fragment>
              )}
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default Modal;
